import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface Notification {
  id: string;
  type: 'download' | 'upload' | 'error' | 'success' | 'info';
  title: string;
  message: string;
  progress?: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  timestamp: Date;
  canDismiss: boolean;
  onCancel?: () => void;
  /** Presigned download URL for completed batch ZIP jobs */
  downloadUrl?: string;
  /** ISO-8601 expiry timestamp for the ZIP presigned URL (optional — absent for legacy records) */
  urlExpiresAt?: string;
  /** Job ID for the batch ZIP download (used to regenerate the URL) */
  jobId?: string;
  /** Callback to regenerate an expired ZIP URL — set by FileBrowser, called from NotificationCenter */
  onRegenerate?: () => void;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  clearCompletedNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>): string => {
    const id = `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    return id;
  }, []);

  const updateNotification = useCallback((id: string, updates: Partial<Notification>) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, ...updates } : n)
    );
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    // Only clear dismissible notifications
    setNotifications(prev => prev.filter(n => 
      !n.canDismiss || n.status === 'pending' || n.status === 'processing'
    ));
  }, []);

  const clearCompletedNotifications = useCallback(() => {
    setNotifications(prev => prev.filter(n => 
      n.status === 'pending' || n.status === 'processing'
    ));
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      updateNotification,
      removeNotification,
      clearAllNotifications,
      clearCompletedNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
